/**
 * Recipes-tab fold: what the kitchen must MAKE on each service day of the
 * selected range, as a look-ahead calendar for batch prep.
 *
 * Order-backed days walk `shipmentsForDay` → `shipmentsToCampuses` with the
 * SAME inclusion + merge rules as the printed recipe pack (`mergeDayRecipes`
 * in print-plan.ts: skip recipe-less components — condiments are NOT skipped
 * by kind — dedupe by (component name, unit), include recipe-backed weekly
 * milk) so on-screen
 * quantities always match what a "Print Recipes" run would produce — this
 * fold additionally keeps the mealtime + school dimensions for row badges.
 *
 * Forecast days (order window still open, no shipments yet) fold the
 * server's `forecastRecipes` rows instead: same "what the kitchen cooks"
 * rule applied server-side, with the estimate kept honestly nullable —
 * mirroring the ingredient rows, a missing history NEVER invents a number.
 */

import type { KitchenShipmentSummary } from '@/api/kitchen-orders'
import type { ShoppingListForecastRecipe } from '@/api/shopping-list'
import { formatRecipeServing } from '@/lib/chefs/format'
import { componentTypeGroup, hotterTemperature, type PackTypeGroup } from '@/lib/chefs/pack-groups'
import { recipeKey } from '@/lib/chefs/print-plan'
import { isoDate } from '@/lib/chefs/service-days'
import { shipmentsForDay, shipmentsToCampuses } from '@/lib/chefs/shipment-to-campus'
import {
  type Component,
  componentKindToProductType,
  type Diet,
  type Mealtime,
  MEALTIME_ORDER,
  type Temperature,
} from '@/lib/chefs/types'

export interface RecipeDayEntry {
  componentVariantId?: string | null
  /** Unique within the day (recipe-pack key space for orders). */
  key: string
  /** Catalog component id — opens `RecipeDetailPanel` (`/api/recipes/:id`). */
  componentId: string
  name: string
  source: 'orders' | 'forecast'
  /** Placed-order quantity in `unit` (recipe-pack semantics); 0 on forecast rows. */
  totalQty: number
  unit: string
  /** Forecast rows: summed history-backed estimate; null = none had history. */
  estimatedServings: number | null
  /** Forecast rows: at least one school×component had no ordering history. */
  forecastMissingHistory: boolean
  /** MEALTIME_ORDER-sorted; empty on forecast rows (menus aren't walked here)
   *  and on weekly-milk rows (campus-level, not tied to one mealtime). */
  mealtimes: Mealtime[]
  /** Sorted, deduped destination school names. */
  schoolNames: string[]
  diets: Diet[]
  /** Recipe portion label ("0.5 cup", "2 Links") off the shipment lines, every
   *  distinct portion joined ("1/2 Cup / 1 Cup") — null on forecast rows
   *  (nothing materialized) and recipe-less lines. */
  servingSize: string | null
  /** Mains / Sides / Condiments / Beverages — the default grouping axis. */
  typeGroup: PackTypeGroup
  /** Canonical temperature of the deliverables this recipe rides in (the
   *  hottest when they differ); null on forecast + weekly-milk rows. */
  temperature: Temperature | null
  /** The day's non-cancelled shipments carrying this component — the targets
   *  for a chef-initiated pre-approved swap (PROD-225). Empty on forecast
   *  rows (nothing materialized to swap yet) and on milk rows keyed by a
   *  projected component. */
  shipmentIds: string[]
}

export interface RecipeDayGroup {
  /** `YYYY-MM-DD`. */
  iso: string
  date: Date
  /** Name-sorted (matches `mergeDayRecipes`); empty = nothing to make. */
  entries: RecipeDayEntry[]
}

interface EntryBucket {
  componentVariantId?: string | null
  key: string
  componentId: string
  name: string
  source: 'orders' | 'forecast'
  totalQty: number
  estimatedKnown: number | null
  forecastMissingHistory: boolean
  unit: string
  mealtimes: Set<Mealtime>
  schoolNames: Set<string>
  diets: Set<Diet>
  shipmentIds: Set<string>
  servingSize: string | null
  typeGroup: PackTypeGroup
  temperature: Temperature | null
}

function finalizeBucket(b: EntryBucket): RecipeDayEntry {
  return {
    key: b.key,
    componentVariantId: b.componentVariantId,
    componentId: b.componentId,
    name: b.name,
    source: b.source,
    totalQty: b.totalQty,
    unit: b.unit,
    estimatedServings: b.estimatedKnown,
    forecastMissingHistory: b.forecastMissingHistory,
    mealtimes: MEALTIME_ORDER.filter((m) => b.mealtimes.has(m)),
    schoolNames: [...b.schoolNames].sort((a, z) => a.localeCompare(z)),
    diets: [...b.diets],
    shipmentIds: [...b.shipmentIds].sort(),
    servingSize: b.servingSize,
    typeGroup: b.typeGroup,
    temperature: b.temperature,
  }
}

/**
 * Build the day-grouped recipe list for the range. `serviceDays` is the
 * page's existing `buildServiceDays` output (one entry per visible day);
 * every day keeps a group — an empty `entries` renders the per-day empty
 * treatment instead of the day silently vanishing.
 */
export function buildRecipeDayGroups(
  serviceDays: ReadonlyArray<{ date: Date }>,
  shipments: ReadonlyArray<KitchenShipmentSummary>,
  forecastRecipes: ReadonlyArray<ShoppingListForecastRecipe>,
): RecipeDayGroup[] {
  const forecastByDay = new Map<string, ShoppingListForecastRecipe[]>()
  for (const f of forecastRecipes) {
    const bucket = forecastByDay.get(f.date)
    if (bucket) {
      bucket.push(f)
    } else {
      forecastByDay.set(f.date, [f])
    }
  }

  return serviceDays.map((day) => {
    const iso = isoDate(day.date)
    const buckets = new Map<string, EntryBucket>()
    const dayShipments = shipmentsForDay(shipments, iso)

    // componentId → the day's swappable (non-cancelled) shipments carrying
    // it. Walked off the raw lines because the campus projection merges
    // deliverables across shipments and drops the shipment linkage.
    const shipmentIdsByComponent = new Map<string, Set<string>>()
    // Recipe portions per component, off the same lines — the recipes tab
    // shows them beside the name so a chef portions without opening the panel.
    // EVERY distinct portion: one recipe can ship at two (a 1/2 Cup and a
    // 1 Cup mac, PROD-1372), and the first one alone mislabels the rest.
    const servingsByComponent = new Map<string, Set<string>>()
    for (const s of dayShipments) {
      if (s.state === 'CANCELLED') {
        continue
      }
      for (const l of s.lines) {
        if (l.componentId === null) {
          continue
        }
        const set = shipmentIdsByComponent.get(l.componentId) ?? new Set<string>()
        set.add(s.id)
        shipmentIdsByComponent.set(l.componentId, set)
        const serving = formatRecipeServing(l.recipeServingQuantity, l.recipeServingUnit) ?? l.servingSize
        if (serving) {
          const servings = servingsByComponent.get(l.componentId) ?? new Set<string>()
          servings.add(serving)
          servingsByComponent.set(l.componentId, servings)
        }
      }
    }

    const addOrdered = (
      component: Component,
      qty: number,
      unit: string,
      mealtime: Mealtime | null,
      school: string,
      temperature: Temperature | null,
    ) => {
      // Same rule as the recipe pack: recipe-less (buy-and-serve)
      // components aren't something the kitchen MAKES. A CONDIMENT is not
      // excluded by kind — a house-made ranch or salsa carries a recipe and
      // the kitchen has to make it; only the packet-of-ketchup kind (no
      // recipe on file) drops out, via the same check.
      if (!component.recipe) {
        return
      }
      const key = recipeKey(component.name, unit)
      let bucket = buckets.get(key)
      if (!bucket) {
        bucket = {
          key,
          componentId: component.id,
          name: component.name,
          source: 'orders',
          totalQty: 0,
          estimatedKnown: null,
          forecastMissingHistory: false,
          unit,
          mealtimes: new Set(),
          schoolNames: new Set(),
          diets: new Set(),
          shipmentIds: shipmentIdsByComponent.get(component.id) ?? new Set(),
          servingSize: [...(servingsByComponent.get(component.id) ?? [])].join(' / ') || null,
          typeGroup: componentTypeGroup(component),
          temperature: null,
        }
        buckets.set(key, bucket)
      }
      bucket.totalQty += qty
      // One recipe can ride in deliverables of different temperatures —
      // the prep bench cares about the hot hold, so the hotter wins.
      bucket.temperature = hotterTemperature(bucket.temperature, temperature)
      if (mealtime !== null) {
        bucket.mealtimes.add(mealtime)
      }
      bucket.schoolNames.add(school)
      for (const diet of component.diets) {
        bucket.diets.add(diet)
      }
    }

    for (const campus of shipmentsToCampuses(dayShipments)) {
      for (const block of campus.mealtimes) {
        for (const deliverable of block.deliverables) {
          for (const component of deliverable.components) {
            addOrdered(
              component,
              deliverable.qty,
              deliverable.unit,
              block.mealtime,
              campus.name,
              deliverable.temperature,
            )
          }
        }
      }
      // The milk card (PROD-1502): recipe-backed milk counts like any other
      // made item (mergeDayRecipes parity — same 'x' unit keeps the key space
      // aligned), per mealtime so moving milk off the mealtime cards changes
      // neither the totals nor the mealtimes a recipe reads.
      for (const milk of campus.milk ?? []) {
        for (const share of milk.byMealtime) {
          addOrdered(milk.component, share.qty, 'x', share.mealtime, campus.name, milk.temperature)
        }
      }
    }

    for (const f of forecastByDay.get(iso) ?? []) {
      const key = `forecast:${f.componentId}:${f.componentVariantId ?? 'default'}`
      let bucket = buckets.get(key)
      if (!bucket) {
        bucket = {
          key,
          componentId: f.componentId,
          componentVariantId: f.componentVariantId,
          name: f.componentName,
          source: 'forecast',
          totalQty: 0,
          estimatedKnown: null,
          forecastMissingHistory: false,
          unit: 'x',
          mealtimes: new Set(),
          schoolNames: new Set(),
          diets: new Set(),
          shipmentIds: new Set(),
          servingSize: null,
          // Forecast rows carry the component's catalog kind — same
          // kind→type projection the shipment fold uses.
          typeGroup: componentTypeGroup({
            productType: componentKindToProductType(f.componentKind),
            isCondiment: f.componentKind === 'CONDIMENT',
          }),
          temperature: null,
        }
        buckets.set(key, bucket)
      }
      bucket.schoolNames.add(f.schoolName)
      if (f.estimatedServings === null) {
        // No history for this school → the summed estimate undercounts;
        // flag it, never invent a quantity (ingredient-row rule).
        bucket.forecastMissingHistory = true
      } else {
        bucket.estimatedKnown = (bucket.estimatedKnown ?? 0) + f.estimatedServings
      }
    }

    const entries = [...buckets.values()].map(finalizeBucket).sort((a, z) => a.name.localeCompare(z.name))
    return { iso, date: day.date, entries }
  })
}

/** Range-level summary for the tab header: distinct recipes across all days. */
export function countRangeRecipes(groups: ReadonlyArray<RecipeDayGroup>): number {
  const keys = new Set<string>()
  for (const group of groups) {
    for (const entry of group.entries) {
      keys.add(recipeKey(entry.name, entry.unit))
    }
  }
  return keys.size
}
